// src/areaAssociados/Eventos.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { FaCalendar, FaMapMarker, FaClock, FaFilter } from 'react-icons/fa';
import api from '../services/api';

const CategoriaColors = {
  esportivo: { bg: '#e3f2fd', border: '#1565c0', text: '#1565c0' },
  cultural: { bg: '#f3e5f5', border: '#7b1fa2', text: '#7b1fa2' },
  social: { bg: '#e8f5e9', border: '#2e7d32', text: '#2e7d32' },
  educacional: { bg: '#fff3e0', border: '#e65100', text: '#e65100' },
  outro: { bg: '#f5f5f5', border: '#616161', text: '#616161' }
};

const linkify = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(https?:\/\/[^\s]+)/gi);
  return parts.map((part, index) => {
    if (!/^https?:\/\//i.test(part)) return part;
    const match = part.match(/^(https?:\/\/[^\s]+?)([.,;:!?)\]]*)$/i);
    const url = match ? match[1] : part;
    const suffix = match ? match[2] : '';
    return (
      <React.Fragment key={index}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1976d2', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {url}
        </a>
        {suffix}
      </React.Fragment>
    );
  });
};

const Eventos = () => {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [imagemExpandida, setImagemExpandida] = useState(false);

  const categorias = [
    { value: '', label: 'Todos' },
    { value: 'esportivo', label: 'Esportivos' },
    { value: 'cultural', label: 'Culturais' },
    { value: 'social', label: 'Sociais' },
    { value: 'educacional', label: 'Educacionais' },
    { value: 'outro', label: 'Outros' }
  ];

  useEffect(() => {
    carregarEventos();
  }, [filtroCategoria]);

  const carregarEventos = async () => {
    try {
      setLoading(true);
      const params = filtroCategoria ? `?categoria=${filtroCategoria}` : '';
      const response = await api.get(`/events/public${params}`);
      if (response.data.success) {
        const sorted = (response.data.events || []).sort((a, b) => 
          new Date(a.dataInicio) - new Date(b.dataInicio)
        );
        setEventos(sorted);
      }
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      setEventos([]);
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data) => {
    if (!data) return '';
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR', { 
      weekday: 'long',
      day: '2-digit', 
      month: 'long', 
      year: 'numeric'
    });
  };

  const formatarHora = (data) => {
    if (!data) return '';
    const d = new Date(data);
    return d.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const formatarDataCurta = (data) => {
    if (!data) return '';
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short' 
    });
  };

  const eventoAbreviado = (evento) => {
    const inicio = new Date(evento.dataInicio);
    const agora = new Date();
    const diffDays = Math.ceil((inicio - agora) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  };

  const diasAteEvento = (data) => {
    const d = new Date(data);
    const agora = new Date();
    agora.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    const diff = Math.ceil((d - agora) / (1000 * 60 * 60 * 24));
    if (diff < 0) return null;
    if (diff === 0) return 'Hoje';
    if (diff === 1) return 'Amanhã';
    if (diff <= 7) return `Em ${diff} dias`;
    return formatarDataCurta(data);
  };

  const categoriasOrdenadas = ['esportivo', 'cultural', 'social', 'educacional', 'outro'];

  const getCategoriaLabel = (cat) => {
    const c = categorias.find(x => x.value === cat);
    return c ? c.label : cat;
  };

  return (
    <Layout title="Agenda de Eventos" showBackButton={true} onBack={() => navigate('/member/dashboard')}>
      <div style={styles.container}>
        {/* Filtro */}
        <div style={styles.filtro}>
          <FaFilter size={16} color="#1A3B7D" />
          <select 
            style={styles.select}
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
          >
            {categorias.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Loading */}
        {loading ? (
          <div style={styles.loading}>Carregando eventos...</div>
        ) : eventos.length === 0 ? (
          <div style={styles.vazio}>
            <FaCalendar size={48} color="#ccc" />
            <p>Nenhum evento agendado</p>
          </div>
        ) : (
          <div style={styles.lista}>
            {eventos.map(evento => (
              <div 
                key={evento.id} 
                style={{
                  ...styles.card,
                  borderLeft: `4px solid ${CategoriaColors[evento.categoria]?.border || '#ccc'}`
                }}
                onClick={() => { setEventoSelecionado(evento); setImagemExpandida(false); }}
              >
                {evento.imagemUrl && (
                  <div style={styles.cardImage}>
                    <img 
                      src={evento.imagemUrl} 
                      alt={evento.titulo}
                      style={styles.cardImg}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                {/* Data do evento - horizontal abaixo da imagem */}
                <div style={styles.cardData}>
                  <div style={styles.dataDia}>{formatarDataCurta(evento.dataInicio)}</div>
                  {diasAteEvento(evento.dataInicio) && (
                    <div style={styles.dataBadge}>{diasAteEvento(evento.dataInicio)}</div>
                  )}
                  <div style={{ fontSize: '13px', color: '#666', fontFamily: 'Montserrat, sans-serif' }}>
                    {formatarHora(evento.dataInicio)}
                  </div>
                </div>

                {/* Conteúdo */}
                <div style={styles.cardContent}>
                  <div style={styles.cardHeader}>
                    <span style={{
                      ...styles.categoriaBadge,
                      backgroundColor: CategoriaColors[evento.categoria]?.bg,
                      color: CategoriaColors[evento.categoria]?.text
                    }}>
                      {getCategoriaLabel(evento.categoria)}
                    </span>
                  </div>

                  <h3 style={styles.titulo}>{evento.titulo}</h3>

                  <div style={styles.info}>
                    {evento.local && (
                      <div style={styles.infoItem}>
                        <FaMapMarker size={14} color="#666" />
                        <span>{evento.local}</span>
                      </div>
                    )}
                  </div>

                  {evento.descricao && (
                    <p style={styles.descricao}>
                      {evento.descricao.length > 100 
                        ? evento.descricao.substring(0, 100) + '...'
                        : evento.descricao}
                    </p>
                  )}

                  {evento.recorrente?.tipo !== 'nenhuma' && (
                    <div style={styles.recorrente}>
                      <FaCalendar size={12} color="#1A3B7D" />
                      <span>Evento recorrente: {evento.recorrente.tipo}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Detalhes do Evento */}
      {eventoSelecionado && (
        <div style={styles.modalOverlay} onClick={() => setEventoSelecionado(null)}>
          <div style={styles.detalhesModalContent} onClick={e => e.stopPropagation()}>
            <button style={styles.fecharModal} onClick={() => setEventoSelecionado(null)}>×</button>
            
            {eventoSelecionado.imagemUrl && (
              <div style={styles.detalhesImage}>
                <img 
                  src={eventoSelecionado.imagemUrl} 
                  alt={eventoSelecionado.titulo}
                  style={{ ...styles.detalhesImg, cursor: 'pointer' }}
                  onClick={() => setImagemExpandida(true)}
                  onError={(e) => e.target.style.display = 'none'}
                />
                <button
                  onClick={() => setImagemExpandida(true)}
                  style={styles.verImagemBtn}
                >
                  Ver imagem completa
                </button>
              </div>
            )}
            
            <div style={styles.detalhesBody}>
              <span style={{
                ...styles.categoriaBadge,
                backgroundColor: CategoriaColors[eventoSelecionado.categoria]?.bg,
                color: CategoriaColors[eventoSelecionado.categoria]?.text,
                marginBottom: '10px'
              }}>
                {getCategoriaLabel(eventoSelecionado.categoria)}
              </span>
              
              <h2 style={styles.detalhesTitulo}>{eventoSelecionado.titulo}</h2>
              
              <div style={styles.detalhesInfo}>
                <div style={styles.detalhesInfoItem}>
                  <FaClock size={16} color="#1A3B7D" />
                  <div>{formatarData(eventoSelecionado.dataInicio)}</div>
                </div>
                {eventoSelecionado.dataFim && (
                  <div style={styles.detalhesInfoItem}>
                    <span>até {formatarData(eventoSelecionado.dataFim)}</span>
                  </div>
                )}
                {eventoSelecionado.local && (
                  <div style={styles.detalhesInfoItem}>
                    <FaMapMarker size={16} color="#1A3B7D" />
                    <div>{eventoSelecionado.local}</div>
                  </div>
                )}
              </div>

              {eventoSelecionado.descricao && (
                <div style={{ ...styles.detalhesDescricao, whiteSpace: 'pre-line' }}>
                  <strong>Descrição</strong>
                  <p style={{ margin: 0 }}>{linkify(eventoSelecionado.descricao)}</p>
                </div>
              )}

              {eventoSelecionado.recorrente?.tipo !== 'nenhuma' && (
                <div style={styles.detalhesRecorrente}>
                  <FaCalendar size={14} color="#1A3B7D" />
                  <span>Evento recorrente: {eventoSelecionado.recorrente.tipo}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {imagemExpandida && eventoSelecionado?.imagemUrl && (
        <div style={styles.lightboxOverlay} onClick={() => setImagemExpandida(false)}>
          <button style={styles.lightboxClose} onClick={() => setImagemExpandida(false)}>×</button>
          <img
            src={eventoSelecionado.imagemUrl}
            alt={eventoSelecionado.titulo}
            style={styles.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Layout>
  );
};

const styles = {
  container: { padding: '20px', maxWidth: '600px', margin: '0 auto' },
  filtro: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '12px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  select: { flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #eee', fontSize: '14px', fontFamily: 'Montserrat, sans-serif', backgroundColor: '#f9f9f9' },
  loading: { textAlign: 'center', padding: '60px', color: '#666', fontFamily: 'Montserrat, sans-serif' },
  vazio: { textAlign: 'center', padding: '60px 20px', color: '#999', fontFamily: 'Montserrat, sans-serif' },
  lista: { display: 'flex', flexDirection: 'column', gap: '16px' },
  card: { backgroundColor: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #eee', transition: 'transform 0.2s', cursor: 'pointer' },
  cardImage: { width: '100%', height: '160px', overflow: 'hidden' },
  cardImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  cardData: { display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 20px', backgroundColor: '#f8f9fa', flexWrap: 'wrap' },
  dataDia: { fontSize: '13px', fontWeight: '700', color: '#1A3B7D', fontFamily: 'Montserrat, sans-serif', textTransform: 'uppercase' },
  dataBadge: { fontSize: '11px', fontWeight: '600', color: '#1A3B7D', backgroundColor: '#e3f2fd', padding: '3px 10px', borderRadius: '12px', fontFamily: 'Montserrat, sans-serif', whiteSpace: 'nowrap' },
  cardContent: { padding: '16px 20px 20px' },
  cardHeader: { marginBottom: '10px' },
  categoriaBadge: { display: 'inline-block', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', fontFamily: 'Montserrat, sans-serif', textTransform: 'uppercase' },
  titulo: { fontSize: '18px', fontWeight: '700', color: '#1A3B7D', marginBottom: '12px', fontFamily: 'Montserrat, sans-serif', lineHeight: '1.3' },
  info: { display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '10px' },
  infoItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#666', fontFamily: 'Montserrat, sans-serif' },
  descricao: { fontSize: '14px', color: '#666', marginTop: '10px', fontFamily: 'Montserrat, sans-serif', lineHeight: '1.5' },
  recorrente: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#1A3B7D', marginTop: '12px', fontFamily: 'Montserrat, sans-serif', fontWeight: '600' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  detalhesModalContent: { backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto', position: 'relative' },
  fecharModal: { position: 'absolute', top: '10px', right: '15px', background: 'none', border: 'none', fontSize: '28px', color: '#666', cursor: 'pointer', zIndex: 10 },
  detalhesImage: { width: '100%', height: '220px', overflow: 'hidden', position: 'relative' },
  detalhesImg: { width: '100%', height: '100%', objectFit: 'cover' },
  verImagemBtn: { position: 'absolute', bottom: '10px', right: '10px', padding: '6px 12px', backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: 'Montserrat, sans-serif', fontWeight: '600' },
  detalhesBody: { padding: '20px' },
  detalhesTitulo: { fontSize: '20px', fontWeight: '700', color: '#1A3B7D', marginBottom: '15px', fontFamily: 'Montserrat, sans-serif' },
  detalhesInfo: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '15px' },
  detalhesInfoItem: { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', color: '#333', fontFamily: 'Montserrat, sans-serif' },
  detalhesDescricao: { fontSize: '14px', color: '#444', fontFamily: 'Montserrat, sans-serif', lineHeight: '1.6', marginBottom: '15px' },
  detalhesRecorrente: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1A3B7D', fontFamily: 'Montserrat, sans-serif', fontWeight: '600' },
  lightboxOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '16px' },
  lightboxImg: { maxWidth: '95vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: '8px' },
  lightboxClose: { position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '26px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }
};

export default Eventos;